import {
  AccessibilityFeatureEnum,
  LifestyleVerticalEnum,
  ListingFilters,
  ParkingFeatureEnum,
  PetsAllowedEnum,
  PropertySubTypeEnum,
  PropertyTypeEnum,
  StandardStatusEnum,
  ViewEnum,
} from '@engelvoelkersamericas/api';
import { LifestyleParam, SelectOption } from '@util/types';
import * as _ from 'lodash';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { ReadonlyURLSearchParams } from 'next/navigation';

export type LifestyleFilters = Partial<
  Record<LifestyleParam, SelectOption<LifestyleVerticalEnum>[]>
>;

export type AmenityOption =
  | 'Pool'
  | 'Heating'
  | 'Fireplace'
  | 'Basement'
  | 'Pet Friendly'
  | 'HOA'
  | 'A/C'
  | 'Accessible'
  | 'Washer/Dryer';

export interface SearchFilterData {
  filterButtons: SelectOption<string>[];
  priceSection: boolean;
  sqftSection: boolean;
  lotSizeSection: boolean;
  storiesSection: boolean;
  yearsSection: boolean;
  listingTypes: SelectOption<PropertyTypeEnum[]>[];
  propertyTypes: SelectOption<PropertySubTypeEnum[]>[];
  bedrooms: SelectOption<number | null>[];
  bathrooms: SelectOption<number>[];
  listingStatus: SelectOption<StandardStatusEnum[]>[];
  garage: {
    vehicles: SelectOption<number | null>[];
    type: SelectOption<ParkingFeatureEnum>[];
  };
  views: string[];
  amenities: AmenityOption[];
  lifestyle: LifestyleFilters;
  states?: string[];
  provinces?: string[];
}

export const propertyTypesToRent: PropertyTypeEnum[] = [
  PropertyTypeEnum.ResidentialLease,
  PropertyTypeEnum.CommercialLease,
];

export const propertyTypesToBuy: PropertyTypeEnum[] = [
  PropertyTypeEnum.Residential,
  PropertyTypeEnum.ResidentialIncome,
  PropertyTypeEnum.Land,
  PropertyTypeEnum.Farm,
  PropertyTypeEnum.CommercialSale,
  PropertyTypeEnum.BusinessOpportunity,
  PropertyTypeEnum.ManufacturedInPark,
];

export const propertyTypesOurListings: PropertyTypeEnum[] = [
  ...propertyTypesToRent,
  ...propertyTypesToBuy,
];

export const defaultListingFilters: ListingFilters = {
  propertyTypes: propertyTypesOurListings,
  listingStatuses: [
    StandardStatusEnum.Active,
    StandardStatusEnum.ActiveUnderContract,
    StandardStatusEnum.Pending,
    StandardStatusEnum.ComingSoon,
  ],
};

export const allLifestyleFilters: LifestyleFilters = {
  water: [
    { title: 'Ocean', value: LifestyleVerticalEnum.WaterOcean },
    { title: 'Lake', value: LifestyleVerticalEnum.WaterLake },
    { title: 'River', value: LifestyleVerticalEnum.WaterRiver },
    { title: 'View', value: LifestyleVerticalEnum.WaterView },
    { title: 'Access', value: LifestyleVerticalEnum.WaterAccess },
    { title: 'Waterfront', value: LifestyleVerticalEnum.WaterWaterfront },
    { title: 'Dock', value: LifestyleVerticalEnum.WaterDock },
    { title: 'Mooring', value: LifestyleVerticalEnum.WaterMooring },
  ],
  golf: [
    { title: 'On Course', value: LifestyleVerticalEnum.GolfOnCourse },
    { title: 'Community', value: LifestyleVerticalEnum.GolfCommunity },
    { title: 'Lot', value: LifestyleVerticalEnum.GolfLot },
  ],
  mountain: [
    { title: 'Mountain Views', value: LifestyleVerticalEnum.MountainMountain },
    { title: 'Water Views', value: LifestyleVerticalEnum.MountainWater },
    { title: 'City Views', value: LifestyleVerticalEnum.MountainCity },
  ],
  wine: [
    { title: 'Wine', value: LifestyleVerticalEnum.WineVineyardWine },
    { title: 'Vineyard', value: LifestyleVerticalEnum.WineVineyardVineyard },
  ],
  ranch: [
    { title: 'Sports/Trails', value: LifestyleVerticalEnum.RanchRecreationalRanchSportsTrails },
    { title: 'Hunting', value: LifestyleVerticalEnum.RanchRecreationalRanchHunting },
    { title: 'Fishing', value: LifestyleVerticalEnum.RanchRecreationalRanchFishing },
    { title: 'Open Land', value: LifestyleVerticalEnum.RanchRecreationalRanchOpenLand },
    { title: 'Ranch Estate', value: LifestyleVerticalEnum.RanchRecreationalRanchRanchEstate },
    {
      title: 'Hospitality/Dude Ranch',
      value: LifestyleVerticalEnum.RanchRecreationalRanchHospitalityDudeRanch,
    },
    { title: 'Livestock', value: LifestyleVerticalEnum.RanchWorkingRanchLivestock },
    { title: 'Agriculture', value: LifestyleVerticalEnum.RanchWorkingRanchAgriculture },
    { title: 'Equestrian', value: LifestyleVerticalEnum.RanchWorkingRanchEquestrian },
  ],

  ski: [{ title: 'Ski In/Ski Out', value: LifestyleVerticalEnum.SkiSkiInSkiOut }],
  equestrian: [{ title: 'Equestrian', value: LifestyleVerticalEnum.Equestrian }],
};

export const setListingQueryParams = (
  router: AppRouterInstance,
  pathname: string,
  newParams: URLSearchParams,
  oldParams?: ReadonlyURLSearchParams,
) => {
  // Maintain existing query parameters if provided
  if (oldParams) {
    Array.from(oldParams.keys()).forEach((key) => {
      if (!newParams.has(key)) {
        newParams.set(key, oldParams.get(key) as string);
      }
    });
  }

  // Navigate to the new URL with updated query parameters
  const query = newParams.toString();
  const newUrl = `${pathname}${query ? `?${query}` : ''}`;

  // Scroll set to false to prevent page from scrolling to top on every rerender
  router.replace(newUrl, { scroll: false });
};

export const getPropertySearchFilterData = (
  hideRentFilters: boolean,
  lifestyle?: LifestyleParam | null,
): SearchFilterData => {
  let finalLifestyleFilters: LifestyleFilters = {};
  if (lifestyle === 'water') {
    finalLifestyleFilters.water = allLifestyleFilters.water;
  } else if (lifestyle === 'golf') {
    finalLifestyleFilters.golf = allLifestyleFilters.golf;
  } else if (lifestyle === 'mountain') {
    finalLifestyleFilters.mountain = allLifestyleFilters.mountain;
  } else if (lifestyle === 'wine') {
    finalLifestyleFilters.wine = allLifestyleFilters.wine;
  } else if (lifestyle === 'ranch') {
    finalLifestyleFilters.ranch = allLifestyleFilters.ranch;
  } // ski and equestrian do not have more than one option so don't show them as possible filters

  const baseData = {
    filterButtons: [
      {
        title: hideRentFilters ? 'For Sale' : 'Listing Type',
        value: 'forSale',
      },
      {
        title: 'Price',
        value: 'price',
      },
      {
        title: 'Beds & Baths',
        value: 'bedsAndBath',
      },
    ],
    priceSection: true,
    sqftSection: true,
    lotSizeSection: true,
    storiesSection: true,
    yearsSection: true,
    listingTypes: [
      { title: 'For Sale', value: propertyTypesToBuy },
      { title: 'For Rent', value: propertyTypesToRent },
    ],
    propertyTypes: [
      {
        title: 'House',
        value: [PropertySubTypeEnum.SingleFamilyResidence, PropertySubTypeEnum.ManufacturedHome],
      },
      {
        title: 'Multi-Family',
        value: [
          PropertySubTypeEnum.MultiFamily,
          PropertySubTypeEnum.Duplex,
          PropertySubTypeEnum.Triplex,
          PropertySubTypeEnum.Quadruplex,
        ],
      },
      { title: 'Condo', value: [PropertySubTypeEnum.Condominium] },
      {
        title: 'Land',
        value: [PropertySubTypeEnum.UnimprovedLand, PropertySubTypeEnum.ManufacturedOnLand],
      },
      { title: 'Co-Op', value: [PropertySubTypeEnum.StockCooperative] },
      {
        title: 'Commercial',
        value: [
          PropertySubTypeEnum.Business,
          PropertySubTypeEnum.MixedUse,
          PropertySubTypeEnum.Office,
          PropertySubTypeEnum.Retail,
          PropertySubTypeEnum.HotelMotel,
          PropertySubTypeEnum.Warehouse,
          PropertySubTypeEnum.Industrial,
          PropertySubTypeEnum.UnimprovedLand,
        ],
      },
      { title: 'Apartment', value: [PropertySubTypeEnum.Apartment] },
      { title: 'Townhouse', value: [PropertySubTypeEnum.Townhouse] },
    ],
    bedrooms: [
      { title: 'Any', value: null },
      { title: 'Studio+', value: 0 },
      { title: '1+', value: 1 },
      { title: '2+', value: 2 },
      { title: '3+', value: 3 },
      { title: '4+', value: 4 },
      { title: '5+', value: 5 },
    ],
    bathrooms: [
      { title: '1+', value: 1 },
      { title: '1.5+', value: 1.5 },
      { title: '2+', value: 2 },
      { title: '2.5+', value: 2.5 },
      { title: '3+', value: 3 },
      { title: '4+', value: 4 },
      { title: '5+', value: 5 },
    ],
    listingStatus: [
      { title: 'Active', value: [StandardStatusEnum.Active] },
      { title: 'Under Contract', value: [StandardStatusEnum.ActiveUnderContract] },
      { title: 'Pending', value: [StandardStatusEnum.Pending] },
      { title: 'Sold', value: [StandardStatusEnum.Closed] },
      { title: 'Coming Soon', value: [StandardStatusEnum.ComingSoon] },
    ],
    garage: {
      vehicles: [
        { title: 'Any', value: null },
        { title: '1+', value: 1 },
        { title: '2+', value: 2 },
        { title: '3+', value: 3 },
        { title: '4+', value: 4 },
        { title: '5+', value: 5 },
      ],
      type: [
        { title: 'Attached', value: ParkingFeatureEnum.Attached },
        { title: 'Detached', value: ParkingFeatureEnum.Detached },
        { title: 'Street', value: ParkingFeatureEnum.OnStreet },
      ],
    },
    views: ['Water', 'City', 'Mountain'],
    amenities: [
      'Pool',
      'Heating',
      'Fireplace',
      'Basement',
      'Pet Friendly',
      'HOA',
      'A/C',
      'Accessible',
      'Washer/Dryer',
    ] as AmenityOption[],
    lifestyle: finalLifestyleFilters,
  };

  return baseData;
};

export const convertQueryParamsToListingFilters = (
  searchParams: ReadonlyURLSearchParams,
  lifestyle: LifestyleParam | null,
): ListingFilters => {
  const filters: ListingFilters = {
    ...defaultListingFilters,
  };

  // Location filters
  filters.country = searchParams.get('country') ?? undefined;
  filters.stateOrProvince = searchParams.get('stateOrProvince') ?? undefined;
  filters.city = searchParams.get('city') ?? undefined;
  filters.cityRegion = searchParams.get('cityRegion') ?? undefined;
  filters.postalCode = searchParams.get('postalCode') ?? undefined;
  filters.streetName = searchParams.get('streetName') ?? undefined;
  filters.streetNumber = searchParams.get('streetNumber') ?? undefined;
  filters.neighborhoodId = searchParams.get('neighborhoodId') ?? undefined;

  // listingTypes determines the property types to show
  const listingTypes = searchParams.get('listingTypes')?.split(',');
  if (!listingTypes || (listingTypes.includes('For Sale') && listingTypes.includes('For Rent'))) {
    filters.propertyTypes = propertyTypesOurListings;
  } else if (listingTypes.includes('For Sale')) {
    filters.propertyTypes = propertyTypesToBuy;
  } else if (listingTypes.includes('For Rent')) {
    filters.propertyTypes = propertyTypesToRent;
  }

  // propertySubTypes. We call it propertyType in the URL so it's a misnomer
  const propertyTypes = searchParams.get('propertyTypes');
  if (propertyTypes) {
    const searchFilterData = getPropertySearchFilterData(false, lifestyle);
    const propertySubTypes: PropertySubTypeEnum[] = [];

    for (const propertyType of propertyTypes.split(',')) {
      const mapping = searchFilterData.propertyTypes.find((p) => p.title === propertyType);
      if (mapping) {
        propertySubTypes.push(...mapping.value);
      }
    }

    if (propertySubTypes.length) {
      filters.propertySubTypes = propertySubTypes;
    }
  }

  // statuses
  const statuses = searchParams.get('listingStatus');
  if (statuses) {
    filters.listingStatuses = statuses.split(',') as StandardStatusEnum[];
  } else {
    // If no statuses are provided, default to all statuses except Closed
    filters.listingStatuses = [
      StandardStatusEnum.Active,
      StandardStatusEnum.ActiveUnderContract,
      StandardStatusEnum.Pending,
      StandardStatusEnum.ComingSoon,
    ];
  }

  // lifestyles
  if (lifestyle) {
    const lifestyles = searchParams
      .get('selectedLifestyles')
      ?.split(',') as LifestyleVerticalEnum[];

    if (!lifestyles) {
      // Lifestyle is selected but there are no query params. Apply all lifestyle verticals for that type.
      filters.lifestyles = allLifestyleFilters[lifestyle]?.map((filter) => filter.value);
    } else {
      // Lifestyle is selected and there are query params. Apply the selected lifestyle verticals.
      filters.lifestyles = lifestyles;
    }
  }

  const minPrice = searchParams.get('minPrice');
  if (minPrice) {
    filters.minListPrice = parseInt(minPrice, 10);
  }

  const maxPrice = searchParams.get('maxPrice');
  if (maxPrice) {
    filters.maxListPrice = parseInt(maxPrice, 10);
  }

  const minBedrooms = searchParams.get('bedrooms');
  if (minBedrooms) {
    filters.minBedrooms = parseInt(minBedrooms, 10);
  }

  const minBathrooms = searchParams.get('bathrooms');
  if (minBathrooms) {
    filters.minBathrooms = parseInt(minBathrooms, 10);
  }

  const minLivingArea = searchParams.get('minSqft');
  if (minLivingArea) {
    filters.minLivingArea = parseInt(minLivingArea, 10);
  }

  const maxLivingArea = searchParams.get('maxSqft');
  if (maxLivingArea) {
    filters.maxLivingArea = parseInt(maxLivingArea, 10);
  }

  const minLotSize = searchParams.get('minLotSize');
  if (minLotSize) {
    filters.minLotSize = parseInt(minLotSize, 10);
  }

  const maxLotSize = searchParams.get('maxLotSize');
  if (maxLotSize) {
    filters.maxLotSize = parseInt(maxLotSize, 10);
  }

  const minGarageSpaces = searchParams.get('garageVehicles');
  if (minGarageSpaces) {
    filters.minGarageSpaces = parseInt(minGarageSpaces, 10);
  }

  const minStories = searchParams.get('minStories');
  if (minStories) {
    filters.minStories = parseInt(minStories, 10);
  }

  const maxStories = searchParams.get('maxStories');
  if (maxStories) {
    filters.maxStories = parseInt(maxStories, 10);
  }

  const minYearBuilt = searchParams.get('minYear');
  if (minYearBuilt) {
    filters.minYearBuilt = parseInt(minYearBuilt, 10);
  }

  const maxYearBuilt = searchParams.get('maxYear');
  if (maxYearBuilt) {
    filters.maxYearBuilt = parseInt(maxYearBuilt, 10);
  }

  const parking = searchParams.get('garageType');
  if (parking) {
    filters.parkingFeatures = parking.split(',') as ParkingFeatureEnum[];
  }

  const views = searchParams.get('views');
  if (views) {
    filters.views = views.split(',') as ViewEnum[];
  }

  const accessibility = searchParams.get('accessibility');
  if (accessibility) {
    filters.accessibilityFeatures = accessibility.split(',') as AccessibilityFeatureEnum[];
  }

  const petFriendly = searchParams.get('petFriendly');
  if (petFriendly === 'true') {
    filters.petFriendlyOptions = [
      PetsAllowedEnum.Yes,
      PetsAllowedEnum.CatsOk,
      PetsAllowedEnum.DogsOk,
    ];
  }

  // Amenities
  const amenities = searchParams.get('selectedAmenities')?.split(',') as
    | AmenityOption[]
    | undefined;
  if (amenities) {
    filters.amenities = {};
    filters.amenities.hasPool = amenities.includes('Pool');
    filters.amenities.hasFireplace = amenities.includes('Fireplace');
    filters.amenities.hasHeating = amenities.includes('Heating');
    filters.amenities.hasCooling = amenities.includes('A/C');
    filters.amenities.hasBasement = amenities.includes('Basement');
    filters.amenities.hasHOA = amenities.includes('HOA');
    filters.amenities.hasWasherDryer = amenities.includes('Washer/Dryer');

    if (amenities.includes('Accessible')) {
      filters.accessibilityFeatures = [
        AccessibilityFeatureEnum.AccessibleEntrance,
        AccessibilityFeatureEnum.AccessibleApproachWithRamp,
        AccessibilityFeatureEnum.AccessibleDoors,
        AccessibilityFeatureEnum.AccessibleFullBath,
        AccessibilityFeatureEnum.AccessibleHallwayS,
      ];
    }
  }

  return filters;
};

export const convertListingFiltersToQueryParams = (
  filters: ListingFilters,
  lifestyle?: LifestyleParam | null,
): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.country) {
    params.set('country', filters.country);
  }
  if (filters.stateOrProvince) {
    params.set('stateOrProvince', filters.stateOrProvince);
  }
  if (filters.city) {
    params.set('city', filters.city);
  }
  if (filters.cityRegion) {
    params.set('cityRegion', filters.cityRegion);
  }
  if (filters.postalCode) {
    params.set('postalCode', filters.postalCode);
  }
  if (filters.neighborhood) {
    params.set('neighborhood', filters.neighborhood);
  }
  if (filters.streetName) {
    params.set('streetName', filters.streetName);
  }
  if (filters.streetNumber) {
    params.set('streetNumber', filters.streetNumber);
  }

  if (filters.neighborhoodId) {
    params.set('neighborhoodId', filters.neighborhoodId);
  }

  if (filters.propertyTypes) {
    if (filters.propertyTypes.join() === propertyTypesToBuy.join()) {
      params.set('listingTypes', 'For Sale');
    } else if (filters.propertyTypes.join() === propertyTypesToRent.join()) {
      params.set('listingTypes', 'For Rent');
    }
  }

  if (filters.propertySubTypes) {
    const searchFilterData = getPropertySearchFilterData(false, lifestyle);
    const selectedPropertyTypeOptions = searchFilterData.propertyTypes.filter((p) =>
      p.value.every((v) => filters.propertySubTypes?.includes(v)),
    );
    const propertyTypeLabels = selectedPropertyTypeOptions.map((o) => o.title).join(',');
    params.set('propertyTypes', propertyTypeLabels);
  }

  if (filters.listingStatuses) {
    if (filters.listingStatuses.join() !== defaultListingFilters.listingStatuses?.join()) {
      const statuses = filters.listingStatuses.join(',');
      params.set('listingStatus', statuses);
    }
  }

  if (filters.lifestyles && lifestyle) {
    // Only apply lifestyle filters if they are different from the default of the selected lifestyle
    const defaultForLifestyle = allLifestyleFilters[lifestyle]?.map((filter) => filter.value);
    if (defaultForLifestyle && !_.isEqual(filters.lifestyles.sort(), defaultForLifestyle.sort())) {
      const lifestyles = filters.lifestyles.join(',');
      params.set('selectedLifestyles', lifestyles);
    }
  }

  if (filters.minListPrice) {
    params.set('minPrice', filters.minListPrice.toString());
  }

  if (filters.maxListPrice) {
    params.set('maxPrice', filters.maxListPrice.toString());
  }

  if (filters.minBedrooms) {
    params.set('bedrooms', filters.minBedrooms.toString());
  }

  if (filters.minBathrooms) {
    params.set('bathrooms', filters.minBathrooms.toString());
  }

  if (filters.minLivingArea) {
    params.set('minSqft', filters.minLivingArea.toString());
  }

  if (filters.maxLivingArea) {
    params.set('maxSqft', filters.maxLivingArea.toString());
  }

  if (filters.minLotSize) {
    params.set('minLotSize', filters.minLotSize.toString());
  }

  if (filters.maxLotSize) {
    params.set('maxLotSize', filters.maxLotSize.toString());
  }

  if (filters.minGarageSpaces) {
    params.set('garageVehicles', filters.minGarageSpaces.toString());
  }

  if (filters.minStories) {
    params.set('minStories', filters.minStories.toString());
  }

  if (filters.maxStories) {
    params.set('maxStories', filters.maxStories.toString());
  }

  if (filters.minYearBuilt) {
    params.set('minYear', filters.minYearBuilt.toString());
  }

  if (filters.maxYearBuilt) {
    params.set('maxYear', filters.maxYearBuilt.toString());
  }

  if (filters.parkingFeatures) {
    const parking = filters.parkingFeatures.join(',');
    params.set('garageType', parking);
  }

  if (filters.views) {
    const views = filters.views.join(',');
    params.set('views', views);
  }

  if (filters.accessibilityFeatures) {
    const accessibility = filters.accessibilityFeatures.join(',');
    params.set('accessibility', accessibility);
  }

  if (filters.petFriendlyOptions?.length) {
    params.set('petFriendly', 'true');
  }

  // Amenities
  if (filters.amenities) {
    const amenities = new Set<AmenityOption>();
    if (filters.amenities.hasPool) {
      amenities.add('Pool');
    }
    if (filters.amenities.hasFireplace) {
      amenities.add('Fireplace');
    }
    if (filters.amenities.hasHeating) {
      amenities.add('Heating');
    }
    if (filters.amenities.hasCooling) {
      amenities.add('A/C');
    }
    if (filters.amenities.hasBasement) {
      amenities.add('Basement');
    }
    if (filters.amenities.hasHOA) {
      amenities.add('HOA');
    }
    if (filters.amenities.hasWasherDryer) {
      amenities.add('Washer/Dryer');
    }
    if (filters.accessibilityFeatures) {
      amenities.add('Accessible');
    }

    if (amenities.size > 0) {
      params.set('selectedAmenities', Array.from(amenities).join(','));
    }
  }

  return params;
};
